import React from "react";
import { useMacroBindings } from "../MacroBindingContext";
import {
  useProgramIteration,
  useRoomIteration,
  usePosIteration,
} from "../MacroCollectionContext";

export interface MacroConditionalBlockProps {
  condition: string;
  showElse: boolean;
  if?: React.FC;
  else?: React.FC;
  puck?: {
    dragRef?: React.Ref<HTMLDivElement>;
  };
}

// Get a binding value, supporting both top-level bindings and collection bindings
function getBindingValue(
  varName: string,
  bindings: any,
  programCtx: ReturnType<typeof useProgramIteration>,
  roomCtx: ReturnType<typeof useRoomIteration>,
  posCtx: ReturnType<typeof usePosIteration>
): any {
  // Check for collection bindings (e.g., "program.Quantity", "room.Nights")
  if (varName.startsWith("program.") && programCtx) {
    const prop = varName.substring(8); // Remove "program."
    switch (prop) {
      case "Days": return programCtx.program.Days;
      case "Quantity": return programCtx.program.Quantity;
      case "index": return programCtx.index;
      case "ordinal": return programCtx.ordinal;
      case "count": return programCtx.count;
      default: return (programCtx.program as any)[prop];
    }
  }

  if (varName.startsWith("room.") && roomCtx) {
    const prop = varName.substring(5); // Remove "room."
    switch (prop) {
      case "Nights": return roomCtx.room.Nights;
      case "Quantity": return roomCtx.room.Quantity;
      case "index": return roomCtx.index;
      case "ordinal": return roomCtx.ordinal;
      case "count": return roomCtx.count;
      default: return (roomCtx.room as any)[prop];
    }
  }

  if (varName.startsWith("pos.") && posCtx) {
    const prop = varName.substring(4); // Remove "pos."
    switch (prop) {
      case "Quantity": return posCtx.posItem.Quantity;
      case "index": return posCtx.index;
      case "ordinal": return posCtx.ordinal;
      case "count": return posCtx.count;
      default: return (posCtx.posItem as any)[prop];
    }
  }

  // Fall back to top-level bindings
  return bindings[varName];
}

// Evaluate a simple condition against binding values
function evaluateCondition(
  condition: string,
  bindings: any,
  programCtx: ReturnType<typeof useProgramIteration>,
  roomCtx: ReturnType<typeof useRoomIteration>,
  posCtx: ReturnType<typeof usePosIteration>
): boolean {
  // Parse simple conditions like "RoomCount > 0", "program.Quantity > 1", etc.
  const trimmed = condition.trim();

  // Handle comparison operators (supports dotted names like program.Quantity)
  const comparisonMatch = trimmed.match(/^([\w.]+)\s*(>|>=|<|<=|==|!=)\s*(\d+)$/);
  if (comparisonMatch) {
    const [, varName, operator, valueStr] = comparisonMatch;
    const bindingValue = getBindingValue(varName, bindings, programCtx, roomCtx, posCtx);
    const compareValue = parseInt(valueStr, 10);

    if (bindingValue === undefined || bindingValue === null) {
      return false;
    }

    const numValue = typeof bindingValue === "number" ? bindingValue : parseInt(String(bindingValue), 10);

    switch (operator) {
      case ">": return numValue > compareValue;
      case ">=": return numValue >= compareValue;
      case "<": return numValue < compareValue;
      case "<=": return numValue <= compareValue;
      case "==": return numValue === compareValue;
      case "!=": return numValue !== compareValue;
      default: return false;
    }
  }

  // Handle simple truthy check (e.g., "RoomCount", "program.Quantity")
  const simpleMatch = trimmed.match(/^([\w.]+)$/);
  if (simpleMatch) {
    const varName = simpleMatch[1];
    const bindingValue = getBindingValue(varName, bindings, programCtx, roomCtx, posCtx);
    return Boolean(bindingValue);
  }

  // Default to false for unrecognized conditions
  return false;
}

const MacroConditionalBlock = {
  label: "Conditional",
  inline: true, // Remove Puck's wrapper div - allows Conditional inside BulletList
  llmDescription: `A conditional block that shows/hides content based on binding values.

IMPORTANT - SLOT DATA MODEL:
This block uses the Puck SLOT pattern (NOT DropZones). The 'if' and 'else' fields are SLOTS that store
child component data INLINE within the props, NOT in a separate zones object.

Correct PuckData structure for Conditional:
{
  "type": "Conditional",
  "props": {
    "id": "cond-1",
    "condition": "DepositPercent < 100",
    "showElse": true,
    "if": [
      { "type": "Html", "props": { "id": "if-content", "content": "<p>Deposit: {{DepositPercent}}%</p>" } }
    ],
    "else": [
      { "type": "Html", "props": { "id": "else-content", "content": "<p>Full payment required</p>" } }
    ]
  }
}

WRONG (do NOT use zones):
{
  "type": "Conditional",
  "props": { "id": "cond-1", "condition": "DepositPercent < 100", "showElse": true }
}
// with separate zones["cond-1:if"] and zones["cond-1:else"] - THIS IS WRONG!

- 'condition' (string): The condition to evaluate
- 'showElse' (boolean): Whether to show an else section (set to true if you need else content)
- 'if' (slot): Content shown when condition is TRUE
- 'else' (slot): Content shown when condition is FALSE (only rendered if showElse: true)

IMPORTANT: This is the ONLY way to do conditional logic. DO NOT use {{#if}} or other template syntax in text blocks.

Supported condition formats:
- Comparison: "RoomCount > 0", "DepositPercent < 100", "program.Quantity > 1"
- Operators: >, >=, <, <=, ==, !=
- Truthy check: "RoomCount" (true if non-zero/non-empty)

Inside repeaters, you can use collection bindings:
- "program.Quantity > 1" - check if program quantity is more than 1
- "room.Quantity > 1" - check room quantity
- "pos.Quantity > 1" - check POS item quantity`,
  fields: {
    condition: {
      type: "text" as const,
      label: "Condition",
    },
    showElse: {
      type: "radio" as const,
      label: "Show Else Section",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    if: {
      type: "slot" as const,
    },
    else: {
      type: "slot" as const,
    },
  },
  defaultProps: {
    condition: "RoomCount > 0",
    showElse: false,
    if: [] as any[],
    else: [] as any[],
  },
  render: (props: MacroConditionalBlockProps) => {
    const { condition, showElse, puck } = props;
    const IfContent = props.if;
    const ElseContent = props.else;
    const bindings = useMacroBindings();
    const programCtx = useProgramIteration();
    const roomCtx = useRoomIteration();
    const posCtx = usePosIteration();

    const conditionMet = evaluateCondition(condition, bindings, programCtx, roomCtx, posCtx);
    const ref = puck?.dragRef as any;

    // Use a React fragment to avoid wrapper divs - inline mode handles dragging
    // The ref is attached to a minimal wrapper only for drag purposes in editor
    return (
      <span ref={ref} data-macro-conditional={condition} style={{ display: "contents" }}>
        {conditionMet && IfContent && <IfContent />}
        {!conditionMet && showElse && ElseContent && <ElseContent />}
      </span>
    );
  },
};

export default MacroConditionalBlock;
